import { makeStyles } from '@material-ui/core/styles'
import useDomDimensions from '../../../hooks/useDomDimensions'
import clsx from 'clsx'
import PetAvatar from '../../../components/Common/PetAvatar'
import moment from 'moment'
import { DialogActions, DialogContent, Icon, MenuItem, Typography } from '@material-ui/core'
import { purple, gray } from '../../../utils/colors'
import { FONT_WEIGHTS, STATUS_ID, EMOJIS } from '../../../utils'
import { useEffect, useMemo, useState } from 'react'
import { IconButton } from '@material-ui/core'
import { api } from '../../../services/api'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import { showError, showSuccess } from '../../../actions/misc/misc.actions'
import { useDispatch } from 'react-redux'
import { DangerButton } from '../../../components/Button/DangerButton'
import DangerConfirmationDialog from '../../../components/Common/Dialogs/DangerConfirmation'
import { MainButton } from '../../../components/Button/MainButton'
import StyledDialog from '../../../components/Common/Dialogs/StyledDialog'
import CheckboxMenu from '../../../components/Common/Menus/CheckboxMenu'
import StyledSelect from '../../../components/Common/StyledSelect'
const alertSound = require('../../../assets/alert.mp3')
import StyledAlert from '../../../components/Common/StyledAlert'
import useCurrentUser from '../../../hooks/useCurrentUser'

// console.log('alertSound', alertSound)

const FORCE_CLEAR_OPTIONS = {
	complete: {
		label: 'Force Complete',
		description: (type) =>
			`This will force complete this ${type} task for all pets. Another ${type} task cannot be created for this visit.`
	},
	delete: {
		label: 'Force Delete',
		description: (type) =>
			`This will delete this ${type} task for all pets. Client or staff will be able to create a new ${type} task for this visit.`
	}
}

const useStyles = makeStyles(() => ({
	root: {
		display: 'flex',
		overflow: 'hidden',
		backgroundColor: '#F9F9F9'
	},
	column: {
		flex: 1,
		overflow: 'scroll',
		borderRight: `1px solid ${gray[400]}`,
		padding: 0,
		display: 'flex',
		flexDirection: 'column',
		height: '100%'
	},
	petHeader: { flex: 1, fontWeight: FONT_WEIGHTS.Bold },
	petRowContainer: { flex: 1, display: 'flex', flexDirection: 'column', gap: '.75rem' },
	iconHeader: { color: purple[600], fontSize: '1.125rem', overflow: 'unset' },
	toggleIcon: { color: purple[600], fontSize: '.8rem', overflow: 'unset', paddingTop: '1px' },
	walkingIcon: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
	row: {
		display: 'flex',
		flexDirection: 'row',
		'& div': {
			display: 'flex'
		},
		'& div.checkin_start': {
			justifyContent: 'center',
			flexBasis: '15%',
			alignItems: 'center',
			fontSize: '14px',
			color: purple[600],
			fontWeight: FONT_WEIGHTS.Bold
		},
		'& div.parking_spot_number': {
			justifyContent: 'center',
			flexBasis: '15%',
			alignItems: 'center',
			fontSize: '24px',
			color: purple[600],
			fontWeight: FONT_WEIGHTS.Bold
		},
		'& div.runner': {
			justifyContent: 'center',
			flexBasis: '25%',
			alignItems: 'center',
			fontSize: '14px',
			color: purple[600],
			fontWeight: FONT_WEIGHTS.Bold
		},
		'& div.actions': {
			display: 'none',
			flexBasis: '30%',
			alignItems: 'center',
			justifyContent: 'center',
			gap: '.5rem',
			padding: '0 .4rem'
		},
		'& > div:first-child': {
			paddingLeft: 10,
			borderRight: `1px solid ${gray[400]}`,
			alignItems: 'flex-start'
		},
		'& > div:nth-child(2)': {
			borderRight: `1px solid ${gray[400]}`
		}
	},
	borderRight: {
		borderRight: `1px solid ${gray[400]}`
	},
	rowHeader: {
		backgroundColor: gray[100],
		borderBottom: `1px solid ${gray[400]}`,
		height: '2.75rem',
		marginBottom: 10,
		'& > div:first-child': {
			alignItems: 'center'
		}
	},
	rowData: {
		backgroundColor: gray[100],
		marginBottom: 5,
		border: `1px solid ${gray[100]}`,
		'& > div:first-child': {
			paddingTop: 8,
			paddingBottom: 8
		}
	},
	rowDataWithActions: {
		'&:hover': {
			border: `1px solid ${purple[200]}`,
			cursor: 'pointer',
			'& > div.checkin_start, & > div.parking_spot_number, & > div.runner': {
				display: 'none'
			},
			'& > div.actions': {
				display: 'flex'
			}
		}
	},
	emptyColumn: {
		display: 'flex',
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		fontWeight: FONT_WEIGHTS.Bold,
		fontSize: '1.2rem',
		color: purple[500]
	},
	narrowColumn: { width: '168px', fontSize: '.75rem' },
	infoSection: {
		borderBottom: `1px solid ${gray[400]}`,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: '1rem',
		flexDirection: 'column',
		gap: '.5rem'
	},
	infoSectionHeader: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
		gap: '.4rem',
		fontWeight: FONT_WEIGHTS.Bold,
		color: purple[800]
	},
	infoSectionData: {
		fontWeight: FONT_WEIGHTS.Bold,
		color: purple[800],
		fontSize: '1.75rem',
		lineHeight: '3rem'
	},
	infoSectionDataSmallContainer: {
		width: '100%',
		justifyContent: 'space-between',
		display: 'flex',
		alignItems: 'center',
		padding: '.2rem 0'
	},
	dataSmallKey: {
		fontWeight: FONT_WEIGHTS.Bold,
		color: purple[800],
		fontSize: '.9rem'
	},
	dataSmallText: {
		fontWeight: FONT_WEIGHTS.Bold,
		color: purple[800],
		fontSize: '1.25rem'
	},
	leaderboardSection: {
		display: 'flex',
		flexDirection: 'column'
	},
	leaderboardSectionHeader: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		fontWeight: FONT_WEIGHTS.Bold,
		fontSize: '.9rem',
		color: purple[800],
		borderBottom: `1px solid ${gray[400]}`,
		padding: '.8rem'
	},
	leaderBoardTable: {
		margin: 0,
		padding: 0,
		borderCollapse: 'collapse',
		marginLeft: 5,
		marginRight: 5,
		marginBottom: '1rem',
		fontSize: '.8rem',
		color: purple[800],
		fontWeight: FONT_WEIGHTS.SemiBold,
		'& > thead > tr': {
			borderBottom: `1px solid ${gray[400]}`,
			height: '2.75rem',
			'& > td:nth-child(1)': {
				width: 'calc(100% - 160px)',
				borderRight: `1px solid ${gray[400]}`,
				paddingLeft: '.75rem',
				'& > div': {
					display: 'flex',
					alignItems: 'center'
				}
			},
			'& > td:nth-child(2)': {
				width: '80px',
				borderRight: `1px solid ${gray[400]}`,
				textAlign: 'center',
				'& > div': {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}
			},
			'& > td:nth-child(3)': {
				width: '80px',
				textAlign: 'center',
				'& > div': {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}
			}
		},
		'& > tbody > tr': {
			borderBottom: `1px solid ${gray[400]}`,
			height: '2.2rem',
			'& > td:nth-child(1)': {
				width: 'calc(100% - 160px)',
				paddingLeft: '.75rem',
				'& > div': {
					display: 'flex',
					alignItems: 'center'
				}
			},
			'& > td:nth-child(2)': {
				width: '80px',
				'& > div': {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}
			},
			'& > td:nth-child(3)': {
				width: '80px',
				'& > div': {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}
			}
		}
	},
	fullScreenButtonContainerNormal: {
		position: 'absolute',
		right: 8,
		bottom: 10,
		background: 'rgba(255,255,255,.4)',
		width: 30,
		height: 30,
		borderRadius: 15,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 100000,
		boxShadow: '0 0 8px 0 rgba(0, 0, 0, .13)'
	},
	taskType: {
		backgroundColor: gray[100],
		fontSize: '.6rem',
		letterSpacing: '.1rem',
		padding: '.3rem .5rem',
		fontWeight: '600',
		textTransform: 'uppercase',
		borderBottom: `1px solid ${gray[400]}`,
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: '.4rem',
		'& > span:nth-child(1), & > span:nth-child(4),  & > span:nth-child(6)': {
			fontSize: '1rem'
		},
		'& > span:nth-child(3)': {
			flex: 1
		},
		'& > span:nth-child(2), & > span:nth-child(5), & > span:nth-child(7)': {
			paddingTop: '1px'
		}
	},
	petAvatarContainer: {
		display: 'flex',
		gap: '1rem',
		alignItems: 'center',
		flex: 1,
		marginRight: '1rem'
	},
	claimDialogContent: {
		display: 'flex',
		flexDirection: 'column',
		padding: '0 1.5rem 1.5rem 1.5rem',
		gap: '1rem',
		flex: 1
	},
	statsColumnHeader: {
		padding: '.5rem',
		display: 'flex',
		gap: '.5rem',
		flexDirection: 'row',
		alignItems: 'center',
		'& > span:nth-child(1)': {
			fontSize: '1rem'
		},
		'& > span:nth-child(2)': {
			paddingTop: '1px',
			fontSize: '.8125rem',
			letterSpacing: '.1rem',
			fontWeight: '600',
			textTransform: 'uppercase'
		}
	},
	statsContainer: {
		overflow: 'scroll',
		display: 'flex',
		flexDirection: 'column'
	},
	forceClearContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '.5rem',
		padding: '.5rem 1rem .5rem 0'
	}
}))

// Created with the help of Claude, ai can have some good uses
const convertMilliseconds = (ms) => {
	// Ensure we're working with a positive number
	ms = Math.abs(ms)

	// Calculate seconds first
	let totalSeconds = Math.floor(ms / 1000)

	// Extract hours, minutes, and seconds in normal ranges
	const hours = Math.floor(totalSeconds / 3600)
	totalSeconds %= 3600

	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60

	return {
		hours: hours,
		minutes: minutes,
		seconds: seconds
	}
}

const formatTime = ({ hours, minutes, seconds }) => {
	let hour = `${hours}`
	let minute = `${minutes}`
	let second = `${seconds}`
	let output = ''
	if (hours === 0) {
		hour = null
	}
	if (hours < 10 && hours > 0) {
		hour = `0${hours}`
	}
	if (minutes < 10) {
		minute = `0${minutes}`
	}
	if (seconds < 10) {
		second = `0${seconds}`
	}

	output = `${minute}:${second}`
	if (hour) {
		output = `${hour}:${output}`
	}
	return output
}

const formatPercentNumber = (decimalValue = 0) => {
	return Math.round(decimalValue * 100)
}

const getDashboardData = async (facilityId, timeZone, dispatch) => {
	if (!facilityId) {
		return
	}
	try {
		const response = await api.get(`/facilities/${facilityId}/tasks/dropoffsPickupsData?timeZone=${timeZone}`)
		const { data } = response
		if (data.data && data.data.length > 0) {
			return data.data[0]
		}
		if (data.errors && data.errors.length > 0) {
			if (dispatch) {
				console.log('error here')
				dispatch(showError(data.errors[0]))
			}
			return null
		}
	} catch (e) {
		if (dispatch) {
			console.log(e)
			dispatch(showError(e))
		}
		return null
	}
}

const removePetsFromTask = async (taskId, pet_ids, facilityId, dispatch) => {
	if (!taskId || !pet_ids.length) {
		return
	}
	try {
		const response = await api.patch(`/facilities/${facilityId}/tasks/${taskId}/remove/pets`, { pet_ids })
		const { data } = response
		if (data.data && data.data.length > 0) {
			const removedPetIds = data.data
			dispatch(showSuccess(`Successfully removed ${removedPetIds.length} pet(s).`))
		}
		if (data.errors && data.errors.length > 0) {
			if (dispatch) {
				dispatch(showError(data.errors[0]))
			}
			return null
		}
	} catch (e) {
		if (dispatch) {
			console.log(e)
			dispatch(showError(e))
		}
		return null
	}
}

const clearTask = async (taskId, facilityId, dispatch) => {
	if (!taskId) {
		return
	}
	try {
		const response = await api.delete(`/facilities/${facilityId}/tasks/${taskId}`)
		const { data } = response
		if (data.data && data.data.length > 0) {
			dispatch(showSuccess(data.data[0]))
		}
		if (data.errors && data.errors.length > 0) {
			if (dispatch) {
				dispatch(showError(data.errors[0]))
			}
			return null
		}
	} catch (e) {
		if (dispatch) {
			console.log(e)
			dispatch(showError(e))
		}
		return null
	}
}

const forceDeleteTask = async (taskId, facilityId, dispatch) => {
	if (!taskId || !facilityId) {
		return
	}
	try {
		const response = await api.delete(`/facilities/${facilityId}/tasks/${taskId}`, { forced: true })
		const { data } = response
		if (data.data && data.data.length > 0) {
			dispatch(showSuccess(data.data[0]))
		}
		if (data.errors && data.errors.length > 0) {
			if (dispatch) {
				dispatch(showError(data.errors[0]))
			}
			return null
		}
	} catch (e) {
		if (dispatch) {
			console.log(e)
			dispatch(showError(e))
		}
		return null
	}
}

const forceCompleteTask = async (taskId, facilityId, dispatch) => {
	if (!taskId || !facilityId) {
		return
	}
	try {
		const response = await api.patch(`/facilities/${facilityId}/tasks/${taskId}/complete`, { forced: true })
		const { data } = response
		if (data.data && data.data.length > 0) {
			dispatch(showSuccess(data.data[0]))
		}
		if (data.errors && data.errors.length > 0) {
			if (dispatch) {
				dispatch(showError(data.errors[0]))
			}
			return null
		}
	} catch (e) {
		if (dispatch) {
			console.log(e)
			dispatch(showError(e))
		}
		return null
	}
}

const claimTask = async ({ taskId, facilityId, petIds, staffId, dispatch }) => {
	if (!taskId) {
		return
	}
	try {
		const response = await api.patch(`/facilities/${facilityId}/tasks/${taskId}/claim`, { petIds, staffId })
		const { data } = response
		if (data.data && data.data.length > 0) {
			dispatch(showSuccess('Added to queue'))
			return true
		}
		if (data.errors && data.errors.length > 0) {
			if (dispatch) {
				dispatch(showError(data.errors[0]))
			}
			return null
		}
	} catch (e) {
		if (dispatch) {
			dispatch(showError(e))
		}
		return null
	}
}

const Dashboard = () => {
	const [isFullscreen, setIsFullscreen] = useState(false)
	const classes = useStyles()
	const { availableContentHeight } = useDomDimensions()
	const { facility, allStaff } = useSelectedFacility()
	const fullscreenEnabled = document.fullscreenEnabled
	const [activeWaiting, setActiveWaiting] = useState([])
	const [activeInProgress, setActiveInProgress] = useState([])
	// const [averageRunTime, setAverageRunTime] = useState('--:--')
	// const [averageWaitTime, setAverageWaitTime] = useState('--:--')
	// const [averageTotalTime, setAverageTotalTime] = useState('--:--')
	const [leaderboardData, setLeaderboardData] = useState([])

	// Dropoff
	const [averageRunTimeDropoff, setAverageRunTimeDropoff] = useState('--:--')
	const [averageWaitTimeDropoff, setAverageWaitTimeDropoff] = useState('--:--')
	const [averageTotalTimeDropoff, setAverageTotalTimeDropoff] = useState('--:--')
	const [lowerThanThresholdDropoff, setLowerThanThresholdDropoff] = useState(0)
	const [greaterThanThresholdDropoff, setGreaterThanThresholdDropoff] = useState(0)
	const [totalDogsCheckedIn, setTotalDogsCheckedIn] = useState(0)
	const [leaderboardDataDropoffs, setLeaderboardDataDropoffs] = useState([])

	// Pickup
	const [averageRunTimePickup, setAverageRunTimePickup] = useState('--:--')
	const [averageWaitTimePickup, setAverageWaitTimePickup] = useState('--:--')
	const [averageTotalTimePickup, setAverageTotalTimePickup] = useState('--:--')
	const [totalDogsLeftToCheckOut, setTotalDogsLeftToCheckOut] = useState(0)
	const [lowerThanThresholdPickup, setLowerThanThresholdPickup] = useState(0)
	const [greaterThanThresholdPickup, setGreaterThanThresholdPickup] = useState(0)

	const [leaderboardDataPickups, setLeaderboardDataPickups] = useState([])

	const [confirmClearTaskDialogOpen, setConfirmClearTaskDialogOpen] = useState(false)
	const [confirmRemovePetsFromTaskDialogOpen, setConfirmRemovePetsFromTaskDialogOpen] = useState(false)
	const [selectedTaskId, setSelectedTaskId] = useState('')
	const [numLoaded, setNumLoaded] = useState(0)
	const [claimDialogOpen, setClaimDialogOpen] = useState(false)
	const [selectedTask, setSelectedTask] = useState(null)
	const [selectedPetIdsToClaim, setSelectedPetIdsToClaim] = useState([])
	const [selectedPetIdsToRemove, setSelectedPetIdsToRemove] = useState([])
	const [selectedStaffMember, setSelectedStaffMember] = useState('')

	const [confirmForceClearTaskDialogOpen, setConfirmForceClearTaskDialogOpen] = useState(false)
	const [forceClearType, setForceClearType] = useState([]) // ['delete'] | ['complete']

	const dispatch = useDispatch()

	const { isBackOfficeUser } = useCurrentUser()
	const canForceDeleteOrComplete = () => isBackOfficeUser || false

	const staff =
		allStaff
			?.filter((staff) => {
				const { active, position, market_id } = staff || {}
				return (
					active === true &&
					[
						'Management',
						'Pet Care Associate',
						'Pet Care Supervisor',
						'Support Associate',
						'Support Supervisor'
					].includes(position) &&
					market_id === facility?.market_id
				)
			})
			.sort((a, b) => a.first_name.localeCompare(b.first_name)) || []

	const selectedTaskHasMedications =
		selectedTask?.queue?.some((queueItem) => queueItem?.medications?.length > 0) || false
	const selectedTaskHasWelcomeDayPets = selectedTask?.has_welcome_day_pets === true || false

	useEffect(() => {
		let pollingInterval = null
		const getData = async () => {
			const dashboardData = await getDashboardData(facility?._id, facility?.time_zone, dispatch)
			setNumLoaded((prev) => prev + 1)
			if (dashboardData) {
				const {
					active = [],
					leaderboardData = [],
					leaderboardDataDropoffs = [],
					leaderboardDataPickups = [],
					avgRunTimeDropoffInMilliseconds = 0,
					avgRunTimePickupInMilliseconds = 0,
					avgTotalTimeDropoffInMilliseconds = 0,
					avgTotalTimePickupInMilliseconds = 0,
					revisedWaitTimeDropoffInMilliseconds = 0,
					revisedWaitTimePickupInMilliseconds = 0,
					percentLessThanOrEqualToThresholdDropoff = 0,
					percentGreaterThanThresholdDropoff = 0,
					percentLessThanOrEqualToThresholdPickup = 0,
					percentGreaterThanThresholdPickup = 0,
					totalDogsCheckedIn = 0,
					totalDogsLeftToCheckOut = 0
				} = dashboardData || {}

				const waiting = []
				const inProgress = []
				for (const task of active) {
					const { queue } = task
					const allPending = queue.every((t) => t.status.id === STATUS_ID.Pending)
					if (allPending) {
						waiting.push(task)
						continue
					}
					const allInProg = queue.every((t) => t.status.id === STATUS_ID.InProgress)
					if (allInProg) {
						queue.forEach((item) => {
							inProgress.push({ ...task, queue: [item] })
						})
						continue
					}

					const waitingPets = queue.filter((t) => t.status.id === STATUS_ID.Pending)
					const inProgPets = queue.filter((t) => t.status.id === STATUS_ID.InProgress)

					if (waitingPets.length > 0) {
						waiting.push({ ...task, queue: waitingPets })
					}
					if (inProgPets.length > 0) {
						inProgress.push({ ...task, queue: inProgPets })
					}
				}

				setActiveWaiting(waiting)
				setActiveInProgress(inProgress)

				setAverageRunTimeDropoff(formatTime(convertMilliseconds(avgRunTimeDropoffInMilliseconds)))
				setAverageRunTimePickup(formatTime(convertMilliseconds(avgRunTimePickupInMilliseconds)))

				setAverageWaitTimeDropoff(formatTime(convertMilliseconds(revisedWaitTimeDropoffInMilliseconds)))
				setAverageWaitTimePickup(formatTime(convertMilliseconds(revisedWaitTimePickupInMilliseconds)))

				setAverageTotalTimeDropoff(formatTime(convertMilliseconds(avgTotalTimeDropoffInMilliseconds)))
				setAverageTotalTimePickup(formatTime(convertMilliseconds(avgTotalTimePickupInMilliseconds)))

				setLeaderboardData(leaderboardData)
				setLeaderboardDataDropoffs(leaderboardDataDropoffs)
				setLeaderboardDataPickups(leaderboardDataPickups)

				setTotalDogsCheckedIn(totalDogsCheckedIn)
				setTotalDogsLeftToCheckOut(totalDogsLeftToCheckOut)

				setLowerThanThresholdDropoff(formatPercentNumber(percentLessThanOrEqualToThresholdDropoff))
				setGreaterThanThresholdDropoff(formatPercentNumber(percentGreaterThanThresholdDropoff))

				setLowerThanThresholdPickup(formatPercentNumber(percentLessThanOrEqualToThresholdPickup))
				setGreaterThanThresholdPickup(formatPercentNumber(percentGreaterThanThresholdPickup))
			} else {
				setActiveWaiting([])
				setActiveInProgress([])
			}
		}

		getData()

		// Only a tab someone can see polls; a hidden tab skips the tick and catches up as soon
		// as it is shown again.
		pollingInterval = setInterval(() => {
			if (document.visibilityState === 'visible') getData()
		}, 5000)

		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') getData()
		}
		document.addEventListener('visibilitychange', onVisibilityChange)

		return () => {
			clearInterval(pollingInterval)
			document.removeEventListener('visibilitychange', onVisibilityChange)
		}
	}, [facility])

	const [prevWaitingIds, setPrevWaitingIds] = useState([])

	useEffect(() => {
		if (activeWaiting.length > 0) {
			const currentWaitingIds = activeWaiting.map((task) => task._id)
			if (currentWaitingIds.some((id) => !prevWaitingIds.includes(id)) && numLoaded > 0) {
				const sound = new Audio(alertSound)
				sound.play()
			}
			setPrevWaitingIds(currentWaitingIds)
		}
	}, [activeWaiting])

	useEffect(() => {
		const fullscreenFunc = () => {
			if (document.fullscreenElement) {
				setIsFullscreen(true)
			} else {
				setIsFullscreen(false)
			}
		}

		document.addEventListener('fullscreenchange', fullscreenFunc)

		return () => document.removeEventListener('fullscreenchange', fullscreenFunc)
	}, [])

	const handleFullScreenToggle = () => {
		if (isFullscreen) {
			document.exitFullscreen()
		} else {
			if (fullscreenEnabled) {
				document.querySelector('#overviewDashboard').requestFullscreen()
			}
		}
	}

	const handleInitiateRemovePetFromTask = (task) => {
		setConfirmRemovePetsFromTaskDialogOpen(true)
		setSelectedTask(task)
		if (task?.queue?.length === 1) {
			setSelectedPetIdsToRemove([task.queue[0]?.pets[0]?._id])
		}
	}

	const handleRemovePetFromTaskCancel = () => {
		setConfirmRemovePetsFromTaskDialogOpen(false)
		setSelectedTask(null)
		setSelectedPetIdsToRemove([])
	}

	const handleInitiateClearTask = (taskId) => {
		setConfirmClearTaskDialogOpen(true)
		setSelectedTaskId(taskId)
	}

	const handleInitiateClaimTask = (task) => {
		setClaimDialogOpen(true)
		if (task?.queue?.length === 1) {
			setSelectedPetIdsToClaim([task.queue[0]?.pets[0]?._id])
		}
		setSelectedTask(task)
	}

	const handleInitiateForceClearTask = (task) => {
		setConfirmForceClearTaskDialogOpen(true)
		setSelectedTask(task)
	}

	const handleForceClearTaskCancelled = () => {
		setConfirmForceClearTaskDialogOpen(false)
		setForceClearType([])
		setSelectedTask(null)
	}

	const handleClearTaskCancelled = () => {
		setSelectedTaskId('')
		setConfirmClearTaskDialogOpen(false)
	}

	const handleClaimTaskCancelled = () => {
		setSelectedTask(null)
		setClaimDialogOpen(false)
		setSelectedPetIdsToClaim([])
		setSelectedStaffMember('')
	}

	const handleForceClearTaskConfirmation = async () => {
		if (forceClearType.length === 0) {
			return
		}
		try {
			const clearTaskType = forceClearType[0]

			if (clearTaskType === 'delete') {
				await forceDeleteTask(selectedTask._id, facility._id, dispatch)
			}
			if (clearTaskType === 'complete') {
				await forceCompleteTask(selectedTask._id, facility._id, dispatch)
			}
			setActiveWaiting((prev) => {
				const updated = [...prev]
				return updated.filter((task) => task._id !== selectedTask._id)
			})
			setActiveInProgress((prev) => {
				const updated = [...prev]
				return updated.filter((task) => task._id !== selectedTask._id)
			})
			setSelectedTask(null)
			setConfirmForceClearTaskDialogOpen(false)
			setForceClearType([])
		} catch (e) {
			dispatch(showError('Error?'))
		}
	}

	const handleRemovePetsFromTaskConfirmation = async () => {
		try {
			await removePetsFromTask(selectedTask._id, selectedPetIdsToRemove, facility._id, dispatch)
			setActiveWaiting((prev) => {
				const updated = [...prev]
				return updated
					.map((task) => {
						if (task._id !== selectedTask._id) {
							return task
						}
						const updatedTask = { ...task }
						updatedTask.queue = updatedTask.queue.filter((queueItem) => {
							const pet_id = queueItem?.pets?.[0]._id
							return !selectedPetIdsToRemove.includes(pet_id)
						})
						if (updatedTask.queue.length === 0) {
							return null
						}
						return updatedTask
					})
					.filter((task) => task !== null)
			})
			setSelectedTask(null)
			setSelectedPetIdsToRemove([])
			setConfirmRemovePetsFromTaskDialogOpen(false)
		} catch (e) {
			dispatch(showError(`Error: ${e}`))
		}
	}

	const handleClearTaskConfirmation = async () => {
		try {
			await clearTask(selectedTaskId, facility._id, dispatch)
			setActiveWaiting((prev) => {
				const updated = [...prev]
				return updated.filter((task) => task._id !== selectedTaskId)
			})
			setSelectedTaskId('')
			setConfirmClearTaskDialogOpen(false)
		} catch (e) {
			dispatch(showError('Error?'))
		}
	}

	const handleClaimTaskConfirmation = async () => {
		try {
			const success = await claimTask({
				taskId: selectedTask._id,
				facilityId: facility._id,
				petIds: selectedPetIdsToClaim,
				staffId: selectedStaffMember,
				dispatch
			})

			if (success) {
				const isAMixedClaim = selectedTask?.queue?.length > selectedPetIdsToClaim.length
				const staffMemberInfo = staff?.find((s) => s._id === selectedStaffMember)
				const staffDetails = [
					{
						_id: selectedStaffMember,
						first_name: staffMemberInfo?.first_name,
						last_name: staffMemberInfo?.last_name
					}
				]

				setActiveWaiting((prev) => {
					if (!isAMixedClaim) {
						return prev.filter((task) => task._id !== selectedTask._id)
					} else {
						const taskPosition = prev.findIndex((task) => task._id === selectedTask._id)
						const updated = [...prev]
						updated[taskPosition] = {
							...updated[taskPosition],
							queue: updated[taskPosition].queue.filter((queueItem) => {
								const pet_id = queueItem?.pets?.[0]._id
								return !selectedPetIdsToClaim.includes(pet_id)
							})
						}
						return updated
					}
				})
				setActiveInProgress((prev) => {
					if (!isAMixedClaim) {
						const updated = [...prev]
						for (const petId of selectedPetIdsToClaim) {
							const updatedTask = { ...selectedTask }
							updatedTask.queue = updatedTask.queue
								.filter((queueItem) => {
									const pet_id = queueItem?.pets?.[0]._id
									return pet_id === petId
								})
								.map((queueItem) => {
									queueItem.staff = staffDetails
									return queueItem
								})
							updated.push(updatedTask)
						}

						return updated
					} else {
						const updated = [...prev]
						const updatedTask = { ...selectedTask }
						updatedTask.queue = updatedTask.queue
							.filter((queueItem) => {
								const pet_id = queueItem?.pets?.[0]._id
								return selectedPetIdsToClaim.includes(pet_id)
							})
							.map((queueItem) => {
								queueItem.staff = staffDetails
								return queueItem
							})
						updated.push(updatedTask)
						return updated
					}
				})
				handleClaimTaskCancelled()
			}
		} catch (e) {
			dispatch(showError(e))
		}
	}

	const waitingColumns = useMemo(() => {
		const columns = [
			{
				header: () => (
					<div key="waiting-header" className={classes.petHeader}>
						Waiting
					</div>
				),
				render: (data = {}) => {
					const { queue } = data || []
					return (
						<div key={`${data._id}-pet-avatar`} className={classes.petRowContainer}>
							{queue?.map((t) => {
								return t.pets.map((pet, i) => {
									return (
										<PetAvatar
											key={`waiting-avatar-${i}`}
											size="small"
											petName={pet.full_name || pet.name}
											imageUrl={pet.image_url}
										/>
									)
								})
							})}
						</div>
					)
				}
			},
			{
				header: () => (
					<div key="waiting-checkin-header" className="checkin_start">
						<Icon className={`${classes.iconHeader} fas fa-watch`} />
					</div>
				),
				render: (data) => {
					const now = moment()
					const targetDate = moment(data.checkin_start)

					const diffInMilliseconds = now.diff(targetDate)

					return (
						<div key={`${data._id}-checkin-start`} className="checkin_start">
							{formatTime(convertMilliseconds(diffInMilliseconds))}
						</div>
					)
				}
			},
			{
				header: () => (
					<div key="waiting-parking-spot-header" className="parking_spot_number">
						<Icon className={`${classes.iconHeader} fas fa-car`} />
					</div>
				),
				render: (data) => {
					if (data.parking_spot_number <= 0) {
						return (
							<div key={`${data._id}-parking_spot_number`} className="parking_spot_number">
								<Icon
									className={`${classes.iconHeader} ${classes.walkingIcon} fas fa-dog-leashed`}
								/>
							</div>
						)
					} else {
						return (
							<div key={`${data._id}-parking_spot_number`} className="parking_spot_number">
								{data.parking_spot_number}
							</div>
						)
					}
				}
			},
			{
				header: () => null,
				render: (data) => {
					const hasAssociatedInProgressTask = activeInProgress.some((task) => task._id === data._id)

					return (
						<div key={`${data._id}-actions`} className="actions">
							<MainButton
								variant="contained"
								size="small"
								label={'Claim'}
								onClick={() => handleInitiateClaimTask(data)}
								disableElevation></MainButton>

							<DangerButton
								variant="contained"
								size="small"
								label={'Clear'}
								onClick={() => {
									if (!hasAssociatedInProgressTask) {
										handleInitiateClearTask(data._id)
									} else {
										handleInitiateRemovePetFromTask(data)
									}
								}}
							/>
						</div>
					)
				}
			}
		]

		return columns
	}, [classes, activeInProgress])

	const inProgressColumns = useMemo(() => {
		const columns = [
			{
				header: () => (
					<div key="in-progress-header" className={classes.petHeader}>
						In Progress
					</div>
				),
				render: (data = {}) => {
					const { queue } = data || []
					return (
						<div key={`${data._id}-inprog-pet-avatar`} className={classes.petRowContainer}>
							{queue?.map((t) => {
								return t.pets.map((pet, i) => {
									return (
										<PetAvatar
											key={`in-progress-avatar-${i}`}
											size="small"
											petName={pet.name}
											imageUrl={pet.image_url}
										/>
									)
								})
							})}
						</div>
					)
				}
			},
			{
				header: () => (
					<div key="in-progress-runner-header" className={`runner ${classes.borderRight}`}>
						<Icon className={`${classes.iconHeader} fas fa-running`} />
					</div>
				),
				render: (data) => {
					const { queue } = data || []

					return (
						<div key={`${data._id}-inprog-runner`} className={`runner ${classes.borderRight}`}>
							{queue?.map((p) => {
								const { staff } = p || [{ first_name: 'Unknown', last_name: 'Unknown' }]
								const [staffMember] = staff
								return (
									<div key={`runner-${staffMember?.first_name}`}>{`${
										staffMember?.first_name
									} ${staffMember?.last_name?.substring(0, 1)}`}</div>
								)
							})}
						</div>
					)
				}
			},
			{
				header: () => (
					<div key="in-progress-checkin-header" className={`checkin_start ${classes.borderRight}`}>
						<Icon className={`${classes.iconHeader} fas fa-watch`} />
					</div>
				),
				render: (data) => {
					const now = moment()
					const targetDate = moment(data.checkin_start)
					const diffInMilliseconds = now.diff(targetDate)
					return (
						<div
							key={`${data._id}-inprog-checkin_start`}
							className={`checkin_start ${classes.borderRight}`}>
							{formatTime(convertMilliseconds(diffInMilliseconds))}
						</div>
					)
				}
			},

			{
				header: () => (
					<div key="waiting-parking-spot-header" className="parking_spot_number">
						<Icon className={`${classes.iconHeader} fas fa-car`} />
					</div>
				),
				render: (data) => {
					if (data.parking_spot_number <= 0) {
						return (
							<div key={`${data._id}-parking_spot_number`} className="parking_spot_number">
								<Icon
									className={`${classes.iconHeader} ${classes.walkingIcon} fas fa-dog-leashed`}
								/>
							</div>
						)
					} else {
						return (
							<div key={`${data._id}-parking_spot_number`} className="parking_spot_number">
								{data.parking_spot_number}
							</div>
						)
					}
				}
			},
			{
				header: () => null, // actions if needed
				render: (data) => {
					if (!canForceDeleteOrComplete) {
						return null
					}
					return (
						<div key={`${data._id}-actions`} className="actions">
							<DangerButton
								variant="contained"
								size="small"
								label="Force Clear"
								onClick={() => {
									handleInitiateForceClearTask(data)
								}}
							/>
						</div>
					)
				}
			}
		]
		return columns
	}, [classes])

	const emptyWaitingMessage = activeWaiting.length <= 0 ? '🙂 All clear!' : ''
	const emptyInprogressMessage =
		activeInProgress.length <= 0 ? (activeWaiting.length > 0 ? '⬅️ Pups are waiting.' : '🙌 Woot!') : ''

	return (
		<div id="overviewDashboard" className={classes.root} style={{ height: `${availableContentHeight}px` }}>
			{fullscreenEnabled ? (
				<div className={classes.fullScreenButtonContainerNormal}>
					<IconButton size="small" onClick={handleFullScreenToggle}>
						<Icon
							className={`${classes.toggleIcon} fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}
							style={{ color: purple[800] }}
						/>
					</IconButton>
				</div>
			) : null}
			<div className={classes.column} style={{ padding: 5, paddingTop: 0 }}>
				<div className={clsx([classes.row, classes.rowHeader])}>
					{waitingColumns.map(({ header }) => {
						return header()
					})}
				</div>
				<div>
					{activeWaiting.map((data, i) => {
						const hasMeds = data?.queue?.some((queueItem) => queueItem?.medications?.length > 0)
						const hasWelcomeDayPets = data?.has_welcome_day_pets === true
						return (
							<>
								<div className={classes.taskType}>
									<span>{EMOJIS[data.type]}</span>
									<span>{data.type === 'dropoff' ? 'Drop-off' : 'Pick-up'}</span>
									<span />
									{hasMeds ? (
										<>
											<span>{EMOJIS['medication']}</span>
											<span>Meds</span>
										</>
									) : null}
									{hasWelcomeDayPets ? (
										<>
											<span>{EMOJIS['welcome_day']}</span>
											<span>WD</span>
										</>
									) : null}
								</div>
								<div
									key={`waiting-row-${i}`}
									className={clsx([classes.row, classes.rowData, classes.rowDataWithActions])}>
									{waitingColumns.map(({ render }) => {
										return render(data)
									})}
								</div>
							</>
						)
					})}
				</div>
				{emptyWaitingMessage > '' ? (
					<div className={classes.emptyColumn}>{emptyWaitingMessage}</div>
				) : null}
			</div>
			<div className={classes.column} style={{ padding: 5, paddingTop: 0 }}>
				<div className={clsx([classes.row, classes.rowHeader])}>
					{inProgressColumns.map(({ header }) => {
						return header()
					})}
				</div>
				<div>
					{activeInProgress.map((data, i) => {
						const hasMeds = data?.queue?.some((queueItem) => queueItem?.medications?.length > 0)
						const hasWelcomeDayPets = data?.has_welcome_day_pets === true

						return (
							<>
								<div className={classes.taskType}>
									<span>{EMOJIS[data.type]}</span>
									<span>{data.type === 'dropoff' ? 'Drop-off' : 'Pick-up'}</span>
									<span />
									{hasMeds ? (
										<>
											<span>{EMOJIS['medication']}</span>
											<span>Meds</span>
										</>
									) : null}
									{hasWelcomeDayPets ? (
										<>
											<span>{EMOJIS['welcome_day']}</span>
											<span>WD</span>
										</>
									) : null}
								</div>
								<div
									key={`in-progress-row-${i}`}
									className={clsx([
										classes.row,
										classes.rowData,
										canForceDeleteOrComplete ? classes.rowDataWithActions : undefined
									])}>
									{inProgressColumns.map(({ render }) => {
										return render(data)
									})}
								</div>
							</>
						)
					})}
				</div>
				{emptyInprogressMessage > '' ? (
					<div className={classes.emptyColumn}>{emptyInprogressMessage}</div>
				) : null}
			</div>
			<div className={classes.statsContainer}>
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					<div className={clsx([classes.column, classes.narrowColumn])}>
						<div className={clsx([classes.infoSection, classes.statsColumnHeader])}>
							<span>{EMOJIS['dropoff']}</span>
							<span>Drop-off</span>
						</div>
						<div className={classes.infoSection}>
							<div className={classes.infoSectionHeader}>Total Dogs Checked-In</div>
							<div className={classes.infoSectionData}>{totalDogsCheckedIn}</div>
						</div>
						{/* <div className={classes.infoSection}>
							<div className={classes.infoSectionHeader}>
								<Icon
									className={`${classes.iconHeader} fas fa-watch`}
									style={{ color: purple[800], fontSize: 14 }}
								/>
								Avg. Claim Time
							</div>
							<div className={classes.infoSectionData}>{averageWaitTimeDropoff}</div>
						</div> */}
						{/* <div className={classes.infoSection}>
							<div className={classes.infoSectionHeader}>
								<Icon
									className={`${classes.iconHeader} fas fa-running`}
									style={{ color: purple[800], fontSize: 14 }}
								/>
								Avg. Fetch Time
							</div>
							<div className={classes.infoSectionData}>{averageRunTimeDropoff}</div>
						</div> */}
						<div className={classes.infoSection}>
							<div className={classes.infoSectionHeader}>
								<Icon
									className={`${classes.iconHeader} fas fa-running`}
									style={{ color: purple[800], fontSize: 14 }}
								/>
								Total Time {'<'} 8 mins
							</div>
							<div className={classes.infoSectionData}>{lowerThanThresholdDropoff}%</div>
							{/* <div className={classes.infoSectionDataSmallContainer}>
								<span className={classes.dataSmallKey}>{'< 8 mins'}</span>
								<span className={classes.dataSmallText}>{lowerThanThresholdDropoff}%</span>
							</div>
							<div className={classes.infoSectionDataSmallContainer}>
								<span className={classes.dataSmallKey}>{'> 8 mins'}</span>
								<span className={classes.dataSmallText}>{greaterThanThresholdDropoff}%</span>
							</div> */}
						</div>
						{/* <div className={classes.infoSection}>
							<div className={classes.infoSectionHeader}>
								<Icon
									className={`${classes.iconHeader} fas fa-hourglass-end`}
									style={{ color: purple[800], fontSize: 14 }}
								/>
								Avg. Total Time
							</div>
							<div className={classes.infoSectionData}>{averageTotalTimeDropoff}</div>
						</div> */}
						{/* <div className={classes.leaderboardSection}>
						<div className={classes.leaderboardSectionHeader}>Leaderboard</div>
						<table className={classes.leaderBoardTable}>
							<thead>
								<tr>
									<td>
										<div>
											<Icon
												className={`${classes.iconHeader} fas fa-running`}
												style={{ color: purple[800] }}
											/>
										</div>
									</td>
									<td>
										<div>
											<Icon
												className={`${classes.iconHeader} fas fa-watch`}
												style={{ color: purple[800] }}
											/>
										</div>
									</td>
									<td>
										<div>
											<Icon
												className={`${classes.iconHeader} fas fa-dog`}
												style={{ color: purple[800] }}
											/>
										</div>
									</td>
								</tr>
							</thead>
							<tbody>
								{leaderboardDataDropoffs.map((data, i) => {
									return (
										<tr key={`leaderboard-row-${i}`}>
											<td>
												<div>{`${data.first_name} ${data.last_name?.substring(
													0,
													1
												)}`}</div>
											</td>
											<td>
												<div>{formatTime(convertMilliseconds(data.avgRunTime))}</div>
											</td>
											<td>
												<div>{data.petCount}</div>
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div> */}
					</div>
					<div className={clsx([classes.column, classes.narrowColumn])}>
						<div className={clsx([classes.infoSection, classes.statsColumnHeader])}>
							<span>{EMOJIS['pickup']}</span>
							<span>Pick-up</span>
						</div>
						<div className={classes.infoSection}>
							<div className={classes.infoSectionHeader}>Left to Check-Out</div>
							<div className={classes.infoSectionData}>{totalDogsLeftToCheckOut}</div>
						</div>
						{/* <div className={classes.infoSection}>
							<div className={classes.infoSectionHeader}>
								<Icon
									className={`${classes.iconHeader} fas fa-watch`}
									style={{ color: purple[800], fontSize: 14 }}
								/>
								Avg. Claim Time
							</div>
							<div className={classes.infoSectionData}>{averageWaitTimePickup}</div>
						</div> */}
						{/* <div className={classes.infoSection}>
							<div className={classes.infoSectionHeader}>
								<Icon
									className={`${classes.iconHeader} fas fa-running`}
									style={{ color: purple[800], fontSize: 14 }}
								/>
								Avg. Fetch Time
							</div>
							<div className={classes.infoSectionData}>{averageRunTimePickup}</div>
						</div> */}
						<div className={classes.infoSection}>
							<div className={classes.infoSectionHeader}>
								<Icon
									className={`${classes.iconHeader} fas fa-running`}
									style={{ color: purple[800], fontSize: 14 }}
								/>
								Total Time {'<'} 8 mins
							</div>
							<div className={classes.infoSectionData}>{lowerThanThresholdPickup}%</div>
							{/* <div className={classes.infoSectionDataSmallContainer}>
								<span className={classes.dataSmallKey}>{'< 8 mins'}</span>
								<span className={classes.dataSmallText}>{lowerThanThresholdPickup}%</span>
							</div>
							<div className={classes.infoSectionDataSmallContainer}>
								<span className={classes.dataSmallKey}>{'> 8 mins'}</span>
								<span className={classes.dataSmallText}>{greaterThanThresholdPickup}%</span>
							</div> */}
						</div>
						{/* <div className={classes.infoSection}>
							<div className={classes.infoSectionHeader}>
								<Icon
									className={`${classes.iconHeader} fas fa-hourglass-end`}
									style={{ color: purple[800], fontSize: 14 }}
								/>
								Avg. Total Time
							</div>
							<div className={classes.infoSectionData}>{averageTotalTimePickup}</div>
						</div> */}
						{/* <div className={classes.leaderboardSection}>
						<div className={classes.leaderboardSectionHeader}>Leaderboard</div>
						<table className={classes.leaderBoardTable}>
							<thead>
								<tr>
									<td>
										<div>
											<Icon
												className={`${classes.iconHeader} fas fa-running`}
												style={{ color: purple[800] }}
											/>
										</div>
									</td>
									<td>
										<div>
											<Icon
												className={`${classes.iconHeader} fas fa-watch`}
												style={{ color: purple[800] }}
											/>
										</div>
									</td>
									<td>
										<div>
											<Icon
												className={`${classes.iconHeader} fas fa-dog`}
												style={{ color: purple[800] }}
											/>
										</div>
									</td>
								</tr>
							</thead>
							<tbody>
								{leaderboardDataPickups.map((data, i) => {
									return (
										<tr key={`leaderboard-row-${i}`}>
											<td>
												<div>{`${data.first_name} ${data.last_name?.substring(
													0,
													1
												)}`}</div>
											</td>
											<td>
												<div>{formatTime(convertMilliseconds(data.avgRunTime))}</div>
											</td>
											<td>
												<div>{data.petCount}</div>
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div> */}
					</div>
				</div>
				<div className={classes.leaderboardSection}>
					<div className={classes.leaderboardSectionHeader}>Leaderboard</div>
					<table className={classes.leaderBoardTable}>
						<thead>
							<tr>
								<td>
									<div>
										<Icon
											className={`${classes.iconHeader} fas fa-running`}
											style={{ color: purple[800] }}
										/>
									</div>
								</td>
								<td>
									<div>
										<Icon
											className={`${classes.iconHeader} fas fa-watch`}
											style={{ color: purple[800] }}
										/>
									</div>
								</td>
								<td>
									<div>
										<Icon
											className={`${classes.iconHeader} fas fa-dog`}
											style={{ color: purple[800] }}
										/>
									</div>
								</td>
							</tr>
						</thead>
						<tbody>
							{leaderboardData.map((data, i) => {
								return (
									<tr key={`leaderboard-row-${i}`}>
										<td>
											<div>{`${data.first_name} ${data.last_name?.substring(0, 1)}`}</div>
										</td>
										<td>
											<div>{formatTime(convertMilliseconds(data.avgRunTime))}</div>
										</td>
										<td>
											<div>{data.petCount}</div>
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
			<DangerConfirmationDialog
				open={confirmClearTaskDialogOpen}
				title="Are you sure?"
				description="Clearing will permanently remove this client from the queue!"
				dangerButtonText="Clear"
				onConfirmDangerousAction={handleClearTaskConfirmation}
				onCancel={handleClearTaskCancelled}
			/>
			<StyledDialog
				title={'Clear pets from task'}
				open={confirmRemovePetsFromTaskDialogOpen}
				onClose={handleRemovePetFromTaskCancel}
				maxWidth={500}
				width={500}
				minWidth={500}>
				<DialogContent className={classes.claimDialogContent}>
					<CheckboxMenu
						header={'Which pets are being removed?'}
						options={selectedTask?.queue?.map((queueItem) => {
							const { pets } = queueItem || []
							const [pet] = pets
							if (pet) {
								const { full_name, image_url, profile_image_file_name, _id } = pet
								return {
									component: (
										<div className={classes.petAvatarContainer}>
											<PetAvatar
												petName={full_name}
												imageUrl={image_url || profile_image_file_name}
											/>
										</div>
									),
									value: _id
								}
							}
						})}
						selectedOptions={selectedPetIdsToRemove}
						onSelectedChanged={(v) => {
							if (v && Array.isArray(v)) {
								setSelectedPetIdsToRemove(v)
							}
						}}
					/>
					<StyledAlert
						type="warning"
						text="Clearing these pets will permanently remove them from the queue!"
					/>
					<div style={{ flex: 1 }} />
					<DialogActions style={{ display: 'flex', justifyContent: 'flex-start' }}>
						<DangerButton
							variant="contained"
							label="Confirm"
							disabled={selectedPetIdsToRemove.length === 0}
							onClick={handleRemovePetsFromTaskConfirmation}
						/>
						<MainButton label="Cancel" onClick={handleRemovePetFromTaskCancel} />
					</DialogActions>
				</DialogContent>
			</StyledDialog>
			<StyledDialog
				title="FORCE CLEAR TASK"
				open={confirmForceClearTaskDialogOpen}
				onClose={handleForceClearTaskCancelled}
				maxWidth={500}
				width={500}
				minWidth={500}
				minHeight={500}>
				<DialogContent className={classes.claimDialogContent}>
					<CheckboxMenu
						header="How do you want to clear this task?"
						options={Object.entries(FORCE_CLEAR_OPTIONS).map(([key, value]) => {
							const { label, description } = value
							return {
								component: (
									<div className={classes.forceClearContainer}>
										<Typography style={{ fontWeight: '600' }}>{label}</Typography>
										<span>{description(selectedTask?.type || '')}</span>
									</div>
								),
								value: key
							}
						})}
						singleSelect
						selectedOptions={forceClearType}
						onSelectedChanged={(v) => {
							if (v && Array.isArray(v)) {
								setForceClearType(v)
							}
						}}
					/>
					<div style={{ flex: 1 }} />
					<DialogActions style={{ display: 'flex', justifyContent: 'flex-start' }}>
						<DangerButton
							variant="contained"
							label="Confirm"
							disabled={forceClearType.length === 0}
							onClick={handleForceClearTaskConfirmation}
						/>
						<MainButton label="Cancel" onClick={handleForceClearTaskCancelled} />
					</DialogActions>
				</DialogContent>
			</StyledDialog>
			<StyledDialog
				title={`Claim ${selectedTask?.type === 'dropoff' ? 'Drop-off' : 'Pick-up'}`}
				open={claimDialogOpen}
				onClose={handleClaimTaskCancelled}
				maxWidth={500}
				width={500}
				minWidth={500}>
				<DialogContent className={classes.claimDialogContent}>
					<CheckboxMenu
						header={'Which pets are being claimed?'}
						options={selectedTask?.queue?.map((queueItem) => {
							const { pets, medications = [] } = queueItem || []
							const [pet] = pets
							const hasMedications = medications.length > 0
							const hasWelcomeDayPets = selectedTask?.has_welcome_day_pets === true
							if (pet) {
								const { full_name, image_url, profile_image_file_name, _id } = pet
								return {
									component: (
										<div className={classes.petAvatarContainer}>
											<PetAvatar
												petName={full_name}
												imageUrl={image_url || profile_image_file_name}
											/>
											{hasMedications ? <span>{EMOJIS['medication']}</span> : null}
											{hasWelcomeDayPets ? <span>{EMOJIS['welcome_day']}</span> : null}
										</div>
									),
									value: _id
								}
							}
						})}
						selectedOptions={selectedPetIdsToClaim}
						onSelectedChanged={(v) => {
							if (v && Array.isArray(v)) {
								setSelectedPetIdsToClaim(v)
							}
						}}
					/>
					<div>
						<StyledSelect
							header="Which team member is claiming?"
							value={selectedStaffMember}
							onChange={(e) => setSelectedStaffMember(e.target.value)}>
							{staff
								.filter((staffMember) => {
									if (!selectedTaskHasMedications) {
										return true
									}
									if (!selectedTaskHasWelcomeDayPets) {
										return true
									}
									const { position } = staffMember || {}
									return ['Management', 'Support Supervisor', 'Pet Care Supervisor'].includes(
										position
									)
								})
								.map((staffMember) => {
									const { _id, full_name } = staffMember
									return (
										<MenuItem key={`ps-${_id}`} value={_id}>
											{full_name}
										</MenuItem>
									)
								})}
						</StyledSelect>
					</div>
					<div style={{ flex: 1 }} />
					<DialogActions style={{ display: 'flex', justifyContent: 'flex-start' }}>
						<MainButton
							variant="contained"
							label="Confirm"
							disabled={selectedPetIdsToClaim.length === 0 || selectedStaffMember === ''}
							onClick={handleClaimTaskConfirmation}
						/>
						<MainButton label="Cancel" onClick={handleClaimTaskCancelled} />
					</DialogActions>
				</DialogContent>
			</StyledDialog>
		</div>
	)
}

export default Dashboard
